import { useState, type FormEvent } from 'react'
import { router, usePage } from '@inertiajs/react'
import AppLayout from '@/layouts/AppLayout'

interface TeamMember {
  id: number
  email: string
  full_name: string
  first_name: string | null
  last_name: string | null
  invitation_status: 'pending' | 'accepted'
  invitation_sent_at: string | null
  invitation_accepted_at: string | null
  created_at: string
}

interface Props {
  team_members: TeamMember[]
  available_seats: number
  team_member_limit: number
}

export default function TeamIndex({ team_members, available_seats, team_member_limit }: Props) {
  const { flash } = usePage<{ flash: { notice?: string; alert?: string } }>().props
  const [sending, setSending] = useState(false)
  const [formData, setFormData] = useState({ email: '', first_name: '', last_name: '' })

  const canInvite = available_seats > 0
  const seatsUsed = team_members.length

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setSending(true)
    router.post(
      '/team_members',
      { team_member: formData },
      {
        onFinish: () => {
          setSending(false)
          setFormData({ email: '', first_name: '', last_name: '' })
        },
      },
    )
  }

  function handleRevoke(member: TeamMember) {
    if (!confirm(`Revoke access for ${member.full_name || member.email}? This cannot be undone.`)) return
    router.delete(`/team_members/${member.id}`)
  }

  function handleResend(member: TeamMember) {
    router.post(`/team_members/${member.id}/resend_invitation`)
  }

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="mt-2 text-sm text-gray-600 leading-relaxed">
            Invite assistants to help manage your LendSolo account. Members can create loans,
            record payments, send offering invitations, and generate reports — but only you
            can lock deals, manage billing, or invite other team members.
          </p>
        </div>

        {flash?.notice && (
          <div className="mb-4 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700">
            {flash.notice}
          </div>
        )}
        {flash?.alert && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
            {flash.alert}
          </div>
        )}

        <div className="mb-5 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {seatsUsed} of {team_member_limit} seats used
          </span>
          {!canInvite && (
            <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
              Limit reached
            </span>
          )}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5 mb-8">
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Invite a team member</h2>

          {!canInvite ? (
            <p className="text-sm text-gray-500">
              You've used all {team_member_limit} available seats. Revoke access for a member to add a new one.
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">First name</label>
                  <input
                    type="text"
                    required
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Last name</label>
                  <input
                    type="text"
                    required
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Email address</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="alice@example.com"
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
              >
                {sending ? 'Sending invitation…' : 'Send invitation'}
              </button>
            </form>
          )}
        </div>

        <div>
          <h2 className="text-sm font-semibold text-gray-800 mb-3">Current members</h2>
          {team_members.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-8 text-center">
              <p className="text-sm text-gray-500">No team members yet. Invite your first assistant above.</p>
            </div>
          ) : (
            <ul className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100 overflow-hidden">
              {team_members.map((member) => (
                <li key={member.id} className="p-4 flex items-center justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate">{member.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{member.email}</p>
                    <p className="text-xs mt-1">
                      {member.invitation_status === 'pending' ? (
                        <span className="text-amber-700 font-medium">
                          Pending
                          {member.invitation_sent_at &&
                            ` — invited ${new Date(member.invitation_sent_at).toLocaleDateString()}`}
                        </span>
                      ) : (
                        <span className="text-emerald-700 font-medium">
                          Active
                          {member.invitation_accepted_at &&
                            ` — joined ${new Date(member.invitation_accepted_at).toLocaleDateString()}`}
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {member.invitation_status === 'pending' && (
                      <button
                        onClick={() => handleResend(member)}
                        className="text-xs font-medium px-3 py-1.5 border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-lg transition-colors"
                      >
                        Resend
                      </button>
                    )}
                    <button
                      onClick={() => handleRevoke(member)}
                      className="text-xs font-medium px-3 py-1.5 border border-red-200 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                    >
                      Revoke
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
